import { convertDateFormat } from '../../../helper/convertDate';
import {
	convertToInternationalCurrencySystem,
	convertToTwoDecimal,
} from '../../../helper/convertAmount';
import { chartTitleChange, yoyChange } from '../../../helper/yoyUtils';
import usStateMapCoordinates from '../../../common/components/map/geo-data/usStateMapCoordinates';

export const getPercentageDiff = (item) => {
	if (Number(item?.lyValue) === 0) {
		return 'NA';
	}
	return convertToTwoDecimal((item?.tyValue / item?.lyValue) * 100) - 100 || 0;
};

export const parseOnHandResponse = (param) => {
	const input = param?.json?.result?.data || [];
	return input?.map((item) => {
		return {
			...item,
			ty_value: item?.tyValue,
			ly_value: item?.lyValue,
			bus_dt: convertDateFormat(item?.busDt),
			week_number: item?.wmYrWkNbr,
			excel_ty_value: item?.tyValue,
			excel_ly_value: item?.lyValue,
			excel_diff_perc: getPercentageDiff(item),
			diff_perc:
				Number(item?.lyValue) === 0
					? 'NA'
					: convertToTwoDecimal((item?.tyValue / item?.lyValue) * 100) - 100 ||
					  0,
			wm_wk_nbr: item?.wmWkNbr,
		};
	});
};

export const parseSummary = (arg) => {
	let result = {};

	if (!arg?.json?.result?.result || !arg?.json?.result?.result.length) {
		result = {
			tyValue: '0',
			yoy: '0% YoY',
		};
	} else {
		const { tyValue, yoy, bus_dt = '' } = arg?.json?.result?.result[0] || {};
		let prefix = '';
		if (yoy > 0) {
			prefix = '+ ';
		}
		result = {
			tyValue,
			yoy: `${prefix}${yoyChange(yoy)}`,
			bus_dt,
		};
	}

	const _getStoreCountAndNilPickRate = () => {
		return {
			...result,
			tyValue: `${convertToInternationalCurrencySystem(result.tyValue)}%`,
		};
	};

	return {
		netSales() {
			return {
				...result,
				tyValue: `$${convertToInternationalCurrencySystem(result.tyValue)}`,
			};
		},
		storeCount() {
			return { ...result, tyValue: `${result.tyValue}` };
		},
		inStock: _getStoreCountAndNilPickRate,
		nilPickRate: _getStoreCountAndNilPickRate,
	};
};

export const parseTrendChartResponse = (param) => {
	const input = param?.json?.result?.data;
	if (!input || !input.length) {
		return { data: null, summary: {} };
	}

	const data = input?.map((item) => {
		return {
			...item,
			bus_dt: item?.busDt,
			cal_wk_day_nm: item?.calWkDayNm,
			ly_value: item?.lyValue,
			ty_value: item?.tyValue,
			wm_wk_nbr: item?.wmWkNbr,
		};
	});

	return {
		data,
		summary: {
			yoy: param?.json?.result?.summary?.yoy,
			total: param?.json?.result?.summary?.total,
		},
	};
};

const getArrayOfLimitLength = (array, LIMIT) => {
	const filledArray = [...Array(LIMIT).keys()]?.map((i) => {
		if (i < array.length) {
			return array[i];
		} else return {};
	});

	return filledArray;
};

export const parseRelativeResponse = (param) => {
	const input = param?.json?.result?.result;
	if (!input || !input.length) {
		return (
			{ Item: null },
			{ Category: null },
			{ Subcategory: null },
			{ 'Brand(Overall)': null }
		);
	}

	return [
		{ Item: input[0]?.item },
		{ Category: input[0]?.category },
		{ Subcategory: input[0]?.subcategory },
		{ 'Brand(Overall)': input[0]?.brand },
	];
};

export const parseMapSummaryResponse = (param) => {
	const data = param?.json?.result?.data;
	const summary = param?.json?.result?.summary;
	const _features = [];

	// error case
	if (!data || !data.length) {
		return {
			stateGeoJSONData: { type: 'FeatureCollection', features: _features },
			summary,
			min: 0,
			max: 0,
		};
	}

	const tyValues = [];
	const yoyValues = [];
	const absYoYValues = [];
	const { features } = usStateMapCoordinates;

	features.forEach(({ properties, geometry }) => {
		const element = data.find((item) =>
			properties?.id
				? properties?.id.toLocaleLowerCase() === item?.id.toLocaleLowerCase()
				: false
		);
		if (element) {
			let change = chartTitleChange(convertToTwoDecimal(element?.yoy));
			if (change !== 'NA') {
				change = Math.sign(element?.yoy) < 0 ? `-${change}` : change;
			}

			_features.push({
				id: properties?.id,
				type: 'Feature',
				geometry: geometry,
				properties: {
					name: properties?.name,
					id: properties?.id,
					absYoY: element?.absYoY,
					tyValue: element?.tyValue,
					lyValue: element?.lyValue,
					yoyValue: element?.yoy,
					yoy: `${chartTitleChange(convertToTwoDecimal(element?.yoy))}`,
					change,
					movement: Math.sign(element?.yoy) < 0 ? 'down' : 'up',
				},
			});
			tyValues.push(element?.tyValue);
			if (element?.yoy !== -999999) yoyValues.push(element?.yoy);
			if (element?.absYoY !== -999999) absYoYValues.push(element?.absYoY);
		} else {
			_features.push({
				id: properties?.id,
				type: 'Feature',
				geometry: geometry,
				properties: {
					name: properties?.name,
					id: properties?.id,
					yoy: -999999,
					absYoY: -999999,
					change: -999999,
					lyValue: 0,
					tyValue: 0,
					yoyValue: -999999,
					movement: 'down',
				},
			});
			tyValues.push(0);
		}
	});

	const tempArray = [...tyValues].sort((a, b) => a - b);
	// Finds min & max for yoy & absYoY
	const yoyChangeArray = [...yoyValues].sort((a, b) => a - b);
	const absYoYChangeArray = [...absYoYValues].sort((a, b) => a - b);
	// Find Top 5 States & Bottom 5 states
	const yoyOrderedStates = [..._features]
		.filter((a) => a?.properties?.yoyValue !== -999999)
		.sort((a, b) => b?.properties?.yoyValue - a?.properties?.yoyValue);
	const absYoyOrderedStates = [..._features]
		.filter((a) => a?.properties?.absYoY !== -999999)
		.sort((a, b) => b?.properties?.absYoY - a?.properties?.absYoY);

	const yoyTopStates = getArrayOfLimitLength(yoyOrderedStates.slice(0, 5), 5);
	const absYoyTopStates = getArrayOfLimitLength(
		absYoyOrderedStates.slice(0, 5),
		5
	);
	const yoyBottomStates = getArrayOfLimitLength(
		yoyOrderedStates.slice(-5).reverse(),
		5
	);
	const absYoyBottomStates = getArrayOfLimitLength(
		absYoyOrderedStates.slice(-5).reverse(),
		5
	);

	return {
		summary,
		min: tempArray[0],
		max: tempArray[tempArray.length - 1],
		yoyTopStates,
		absYoyTopStates,
		yoyBottomStates,
		absYoyBottomStates,
		minYoY: yoyChangeArray[0],
		maxYoY: yoyChangeArray[yoyChangeArray.length - 1],
		minAbsYoY: absYoYChangeArray[0],
		maxAbsYoY: absYoYChangeArray[absYoYChangeArray.length - 1],
		stateGeoJSONData: { type: 'FeatureCollection', features: _features },
	};
};

export const getUrlParams = (subCatId, brandId) => {
	let urlIdParams;
	if (subCatId) {
		urlIdParams = `&subCatId=${subCatId}`;
	} else if (brandId) {
		urlIdParams = `&brandId=${brandId}`;
	} else {
		urlIdParams = '';
	}
	return urlIdParams;
};

export const getKpiType = {
	netSales: 'NET_SALES',
	units: 'SALES_VOLUME',
	gmv: 'GMV',
	onhandinventory: 'ON_HAND',
	intransitinventory: 'ON_ORDER',
	netsales: 'NET_SALES',
	instockinventory: 'IN_STOCK',
};
